import { useCallback, useEffect, useMemo, useState } from 'react';
import { auth, collection, database, doc, onSnapshot, updateDoc } from '../Utils/firebaseConfig';
import { classifyInboxItems } from '../Utils/logbookInbox';
import { useAuth } from './useAuth';

function listenViaElectron(uid, setItems) {
  const api = typeof window !== 'undefined' ? window.electronAPI : null;
  if (!api?.startLogbookInboxListener || !api?.onLogbookInboxUpdate) return null;

  const unsubUpdate = api.onLogbookInboxUpdate((next) => {
    setItems(Array.isArray(next) ? next : []);
  });
  void api.startLogbookInboxListener(uid);

  return () => {
    try { unsubUpdate?.(); } catch (_) {}
    try { api.stopLogbookInboxListener?.(); } catch (_) {}
  };
}

export default function useLogbookInbox() {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!uid) {
      setItems([]);
      return undefined;
    }

    const electronUnsub = listenViaElectron(uid, setItems);
    if (electronUnsub) return electronUnsub;

    let cancelled = false;
    let unsubscribe = () => {};
    let retryTimer = 0;
    let attempts = 0;

    const listen = async () => {
      try {
        if (auth.authStateReady) await auth.authStateReady();
        await (auth.currentUser || user).getIdToken();
      } catch (_) {}

      if (cancelled) return;
      unsubscribe();

      const itemsRef = collection(database, 'logbook', 'app', 'notifications', uid, 'items');
      unsubscribe = onSnapshot(
        itemsRef,
        (snapshot) => {
          attempts = 0;
          setItems(snapshot.docs.map((itemDoc) => ({
            sourceKey: itemDoc.id,
            ...itemDoc.data(),
          })));
        },
        (error) => {
          const code = error?.code || error?.message || error;
          if (!cancelled && code === 'permission-denied' && attempts < 4) {
            attempts += 1;
            retryTimer = window.setTimeout(listen, 400 * attempts);
            return;
          }
          console.warn('[useLogbookInbox] snapshot failed:', code, { uid });
          if (!cancelled) setItems([]);
        }
      );
    };

    listen();

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
      unsubscribe();
    };
  }, [uid, user]);

  const classified = useMemo(() => classifyInboxItems(items), [items]);

  const dismiss = useCallback(async (sourceKey, phase) => {
    if (!uid || !sourceKey || !phase) return;
    try {
      if (typeof window !== 'undefined' && window.electronAPI?.dismissLogbookInboxItem) {
        const result = await window.electronAPI.dismissLogbookInboxItem(sourceKey, phase);
        if (!result?.success) {
          console.warn('[useLogbookInbox] electron dismiss failed:', result?.error);
        }
        return;
      }
      const itemRef = doc(database, 'logbook', 'app', 'notifications', uid, 'items', sourceKey);
      await updateDoc(itemRef, { acknowledgedPhase: phase });
    } catch (error) {
      console.warn('[useLogbookInbox] dismiss failed:', error?.code || error?.message || error);
    }
  }, [uid]);

  return {
    unread: classified.unread,
    seen: classified.seen,
    badgeCount: classified.badgeCount,
    dismiss,
  };
}
